import { Component, forwardRef, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { CategoryService } from '../services/category.service';
import { Observable } from 'rxjs/Observable';
import { CreateCategoryModal } from '../modals/create/create.modal';
import { EditCategoryModal } from '../modals/edit/edit.modal';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomElement } from '../../forms/elements/classes/custom-element.class';
import { Collection } from '../../support/collection/collection';
import { ModalService } from '../../modals/services/modal.service';
import { AlertService } from '../../alerts/services/alert.service';
import { CategoryModel } from '../models/category.model';
import { TreeComponent } from 'angular-tree-component';
import { Model } from '@kaiser-software/chassis-core';
import { collect } from '@kaiser-software/chassis-core/src/app/support/helpers/collection';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CategoriesComponent),
    multi: true
};

@Component({
    selector: 'chassis-categories',
    templateUrl: './categories.component.html',
    styleUrls: ['./categories.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class CategoriesComponent extends CustomElement implements OnInit {
    @Input() nodes: Array<any>;

    @ViewChild(TreeComponent)
    protected tree: TreeComponent;

    public categories: Collection;

    /**
     * The 'angular-tree' options.
     * @see https://angular2-tree.readme.io/docs/drag-drop#options
     */
    public options;

    constructor(
        protected alertService: AlertService,
        protected categoryService: CategoryService,
        protected injector: Injector,
        protected modalService: ModalService
    ) {
        super();

        this.options = {
            allowDrag: this.readonly !== true,
            allowDrop: this.readonly !== true
        };
    }

    public ngOnInit(): void {
        this.loadCategories();
    }

    public loadCategories() {
        if (this.nodes) {
            this.categories = collect(this.nodes);
        } else {
            this.retrieveAllCategories().subscribe(categories => this.categories = categories);
        }
    }

    /**
     * Create a category
     */
    public createCategory() {
        const modal = this.modalService.open(CreateCategoryModal, {});

        modal.result.then((category: any) => {
            this.categories.push(category);

            // Update tree
            this.tree.treeModel.update();
        });
    }

    /**
     * Edit a category
     *
     * @param {number} id
     */
    public editCategory(id: number) {
        this.retrieveCategory(id).subscribe(category => {
            const modal = this.modalService.open(EditCategoryModal, {
                category: category.clone(),
            });

            modal.dismissing((resolve, reject, reason) => {
                if (reason === 'delete') {
                    return this.beforeDismissDeleteCallback(resolve, reject, reason, category);
                } else {
                    resolve(reason);
                }
            });

            modal.result.then((result: any) => {
                category.fill(result);

                // Update tree
                this.tree.treeModel.update();
            }, (reason) => {
                if (reason === 'delete') {
                    this.categories = this.categories.filter((item) => {
                        return item !== category;
                    });

                    // Update tree
                    this.tree.treeModel.update();
                }
            });
        });
    }

    /**
     * Delete a category
     *
     * @param {number} id
     */
    public deleteCategory(id: number) {
        const prompt = this.alertService.open({
            title: 'Are you sure?',
        });

        prompt.result.then(() => {
            this.destroyNode(id).subscribe((response) => {
                this.loadCategories();

                // Update tree
                this.tree.treeModel.update();
            });
        });
    }

    /**
     * Actions that needs to happen when moveNode is called on the tree.
     * (Typically - when drag and dropping a node.)
     * @param $event
     * @see https://angular2-tree.readme.io/docs/events#movenode
     */
    public onMoveNode($event): void {
        const isRoot = $event.to.parent.virtual;
        const node   = $event.node;
        const parent = isRoot ? null : $event.to.parent.id;
        const index  = $event.to.index;

        this.updateNodeParent(node, parent, index);
    }

    /**
     * Actions that needs to happen when a key up in a namebox (input
     * for a category name) has been detected.
     * @param node
     * @param $event
     */
    public onKeyUpNamebox(node, $event): void {
        const keyThatHasBeenPressed = $event.key;
        const namebox               = $event.target;
        if (keyThatHasBeenPressed === 'Enter') {
            node.setIsActive(false);
            node.blur();
            namebox.blur(); // This automatically calls the onBlurNamebox().
        }
    }

    /**
     * Actions that needs to happen when a namebox (input for a category
     * name) has lost focus.
     * @param node
     * @param $event
     */
    public onBlurNamebox(node, $event): void {
        const newName = $event.target.value;
        const oldName = node.data.name;

        if (newName !== oldName) {
            this.updateNodeName(node, newName);
        }
    }

    /**
     * Actions that needs to happen when a node has been clicked.
     * @param node
     * @param $event
     */
    public onClickNode(node, $event): void {
    }

    public retrieveAllCategories(): Observable<any> {
        return this.categoryService.all();
    }

    /**
     * @param {number} id - The category that should be retrieved.
     * @returns {Observable<any>}
     */
    public retrieveCategory(id): Observable<any> {
        return this.categoryService.one(id);
    }

    /**
     * Call an update for the node's parent.
     * @param node - The node that needs to be updated.
     * @param parent - The node's new parent, null in case of root.
     */
    public updateNodeParent(node, parent, index = 0): void {
        const id   = node.id;
        const data = {
            parent_id: parent,
            index: index
        };

        this.updateNode(id, data).subscribe((response) => {
        });
    }

    /**
     * Call an update for the node's name.
     * @param node - The node that needs to be updated.
     * @param name - The node's new name.
     */
    public updateNodeName(node, name): void {
        const id   = node.id;
        const data = {
            name: name
        };

        this.updateNode(id, data).subscribe((response) => {
            node.data.name = response.name;
        });
    }

    /**
     * Triggered when checkbox value changed
     *
     * @param value
     * @param node
     */
    public checkboxNodeChanged(value, node) {

        this.selectChildren(node.id, this.categories.all(), value);
        this.setNgModelValue();

    }

    /**
     * Set the ngModelValue -> two-way-bind
     */
    public setNgModelValue() {
        this.val = this.getCheckedCategories(this.categories.all());
    }

    /**
     * Get an array with all checked categories
     *
     * @param categories
     * @param checked
     * @returns {Array}
     */
    protected getCheckedCategories(categories, checked = []) {
        categories.forEach(category => {
            this.getCheckedCategories(category.children, checked);

            if (category.checked === true) {
                const cat = {id: category.id, name: category.name};
                checked.push(cat);
            }
        });

        return checked;
    }

    /**
     * Select all checkboxes of children from the given parent node id
     *
     * @param parentId
     * @param categories
     * @param check
     * @param inParent
     */
    public selectChildren(parentId, categories, check = false, inParent = false) {
        categories.forEach(category => {
            const matchParent = inParent || category.id === parentId;

            this.selectChildren(parentId, category.children, check, matchParent);

            if (matchParent) {
                category.checked = check;
            }
        });
    }

    /**
     * Push the updates to this node to the server.
     * @param {number} id - The id of the node that needs to be updated.
     * @param data - The data which needs to be updated (only updated fields).
     */
    private updateNode(id, data) {
        return this.categoryService.put(id, data);
    }

    /**
     * Delete the node.
     * @param id
     */
    private destroyNode(id) {
        return this.categoryService.delete(id);
    }

    /**
     * Store the new node.
     */
    private storeNode() {

    }

    /**
     * Update an existing category
     *
     * @param {number} id
     * @param {Model | Array<any>} data
     * @param {Collection | Array<any>} categories
     */
    protected updateCategory(id: number, data: Model | Array<any>, categories: Collection | Array<any>) {
        if (categories instanceof Collection) {
            categories.each(category => {
                if (category.id === id) {
                    this.updateCategoryData(category, data);

                    return;
                } else if (category.hasOwnProperty('children') && category.children.length > 0) {
                    this.updateCategory(id, data, category.children);
                }
            });
        } else { // categories = Array
            categories.forEach(category => {
                if (category.id === id) {
                    this.updateCategoryData(category, data);

                    return;
                } else if (category.hasOwnProperty('children') && category.children.length > 0) {
                    this.updateCategory(id, data, category.children);
                }
            });
        }
    }

    /**
     * Update an existing category's data
     *
     * @param {any} category
     * @param {Model | Array<any>} data
     */
    protected updateCategoryData(category: any, data: Model | Array<any>) {
        category.name        = data['name'];
        category.description = data['description'];
    }

    /**
     * Deletes the instance returned from the modal.
     *
     * @param {Function} resolve
     * @param {Function} reject
     * @param reason
     * @param {CategoryModel} category
     */
    protected beforeDismissDeleteCallback(resolve: Function, reject: Function, reason: any, category: CategoryModel) {
        const prompt = this.alertService.open({
            title: 'Are you sure?',
        });

        prompt.result.then(() => {
            if (category.exists()) {
                category.delete().subscribe(() => {
                    resolve(reason);
                }, (error) => {
                    reject(error);
                });
            } else {
                resolve(reason);
            }
        }, (error) => {
            reject(error);
        });
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/categories/components/categories.component.ts